import React, { useState, useEffect } from 'react';
import { Form } from 'react-form';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Input } from '../../../components/Form';
import Img from '../../../components/Img';
import InfoButton from '../../../components/InfoButton';

import {
    createReactFormValidator,
    required,
} from '../../../utils/validation';
import { magicLinkCooldownKey, magicLinkCoolDownTime, magicLinkTimeout } from './utils';
import styles from '../Login/login.scss';

const LoginOptionsStep = ({ onPasswordSubmit, isSubmitting, email, onSubmitMagicLink }) => {
    const auth = useSelector((state) => state?.auth);
    const [shouldDisableSendLink, setShouldDisableSendLink] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const defaultValues = {
        password: '',
    };

    // Function to calculate remaining time based on stored end time
    const calculateRemainingTime = () => {
        if (!email) {
            return 0;
        }

        const cooldownKey = `${magicLinkCooldownKey}_${email}`;
        const storedEndTime = localStorage.getItem(cooldownKey);

        if (!storedEndTime) {
            return 0;
        }

        const endTime = parseInt(storedEndTime, 10);
        const currentTime = Date.now();
        const remainingTime = Math.ceil((endTime - currentTime) / 1000);

        return Math.max(0, remainingTime);
    };

    // Check for existing cooldown and set up timer
    useEffect(() => {
        if (!email) {
            return;
        }

        const updateCountdown = () => {
            const remainingTime = calculateRemainingTime();

            if (remainingTime > 0) {
                setShouldDisableSendLink(true);
                setCountdown(remainingTime);
            } else {
                setShouldDisableSendLink(false);
                setCountdown(0);
                const cooldownKey = `${magicLinkCooldownKey}_${email}`;
                localStorage.removeItem(cooldownKey);
            }
        };

        // Initial check
        updateCountdown();

        // Set up interval to update every second
        const interval = setInterval(updateCountdown, 1000);

        // Handle page visibility changes to immediately update when tab becomes active
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                updateCountdown();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [email]);

    const handleOnClickSendLink = () => {
        const cooldownKey = `${magicLinkCooldownKey}_${email}`;
        const endTime = Date.now() + (magicLinkCoolDownTime * 1000); // 60 seconds from now

        localStorage.setItem(cooldownKey, endTime.toString());
        setShouldDisableSendLink(true);
        setCountdown(magicLinkCoolDownTime);

        onSubmitMagicLink();
    };

    return (
        <div>
            {shouldDisableSendLink && (
                <p className={`${styles.magicLinkCountdownText}`} data-testid="magic-link-login.login-options.countdown-text">
                    {`Need another link? You can try again in `}<span className="fw600">{countdown}</span>{` seconds.`}
                </p>
            )}
            <div className="d-flex jcc mb12 align-items-baseline mb12">
                <p className={`${styles.magicLinkText}`} data-testid="magic-link-login.login-options.magic-link-heading">
                    Sign in Without a Password:
                </p>
                <InfoButton
                    hoverText={(
                        <>
                            <p className={`${styles.magicLinkInfoTitle}`}>Skip the Password</p>
                            <p className={`${styles.magicLinkInfoText}`}>{`If an email exists for this account, we will send a secure sign-in link to your email address for instant access that expires after ${magicLinkTimeout} minutes. Click it to sign right in.`}</p>
                        </>
                    )}
                />
            </div>
            <div>
                <button
                    type="button"
                    className={`btn btn-primary btn-xl btn-block fw600`}
                    disabled={shouldDisableSendLink}
                    onClick={handleOnClickSendLink}
                    data-testid="magic-link-login.login-options.send-link-btn"
                >
                    SEND A LINK
                </button>
            </div>
            {email && (
                <div className="d-flex jcc aic mt12 gap-8">
                    <Img src="/img/v4/icon-email.svg" data-testid="magic-link-login.login-options.email-icon" />
                    <p className={`${styles.magicLinkDisplayEmail}`} data-testid="magic-link-login.login-options.display-email">{email}</p>
                </div>
            )}
            <hr className="hr-text mt32 mb32" data-content="or" />
            <p className={`${styles.magicLinkText}`} data-testid="magic-link-login.login-options.password-heading">Enter Password</p>
            {auth?.error && (
                <p className={styles.errorText} data-testid="magic-link-login.login-options.error-text">
                    {auth.error}
                </p>
            )}
            <Form
                onSubmit={(values) => onPasswordSubmit(values)}
                defaultValues={defaultValues}
            >
                {(formApi) => {
                    const { submitForm } = formApi;
                    const handleFormSubmit = (event) => {
                        event.preventDefault();
                        submitForm();
                    };
                    return (
                        <form onSubmit={handleFormSubmit} data-testid="magic-link-login.login-options.form">
                            <Input
                                id="passwordInput"
                                field="password"
                                type="password"
                                validate={createReactFormValidator(required)}
                                label={`Password`}
                                hiddenLabel={true}
                                disableProfanityCheck={true}
                                themeV3
                                data-testid="magic-link-login.login-options.password-input"
                            />

                            <div>
                                <button
                                    type="submit"
                                    className={`btn btn-primary btn-xl btn-block fw600`}
                                    disabled={isSubmitting}
                                    data-testid="magic-link-login.login-options.sign-in-btn"
                                >
                                    SIGN IN
                                </button>
                            </div>
                        </form>
                    );
                }}
            </Form>
            <a className={`${styles.forgotLink}`} href={`/forgotpassword`} data-testid="magic-link-login.login-options.forgot-password-link">Forgot Password?</a>
        </div>
    );
};

LoginOptionsStep.propTypes = {
    onPasswordSubmit: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
    email: PropTypes.string,
    onSubmitMagicLink: PropTypes.func.isRequired,
};

LoginOptionsStep.defaultProps = {
    isSubmitting: false,
    email: '',
};

export default LoginOptionsStep;
